import { api } from './api'

/**
 * 用户资料（昵称 / 头像）数据层。
 *
 * 与迁移前的差异：
 *   1. 昵称不再写两份。原 Supabase 要同时更新 `profiles.nickname` 和
 *      `auth.updateUser({data:{nickname}})` —— 后者是给 displayName() 读的
 *      user_metadata。现在昵称在 profiles 一处，displayName 直接读接口返回值，
 *      不需要冗余同步。
 *   2. 头像从 Supabase Storage 改到后端本地磁盘（见 core/views_site.py
 *      里"为什么不用对象存储"），URL 由后端生成。
 */

export type Profile = {
	nickname: string | null
	avatar_url: string | null
}

/** 读当前用户资料。读不到时回落空资料，不抛。 */
export async function fetchProfile(): Promise<Profile> {
	try {
		const data = await api.get<Profile>('/profile')
		return data
	} catch {
		console.warn('资料读取失败，回落空资料')
		return {nickname: null, avatar_url: null}
	}
}

/**
 * 保存昵称。空串 = 清空昵称（落回邮箱前缀作默认名）。
 */
export async function saveNickname(nickname: string): Promise<void> {
	await api.post('/profile/update', {nickname: nickname.trim()})
}

/**
 * 上传头像并返回公开 URL。
 *
 * 固定文件名 `<user_id>/avatar.<ext>`，重复上传即覆盖，不会攒出一堆历史头像。
 * 追加时间戳破缓存：同一路径覆盖后，旧 URL 仍可能被浏览器缓存。
 */
export async function uploadAvatar(file: File): Promise<string> {
	// 前端也校验一遍：省一次往返，且给用户的报错更直接。
	// 后端也有一份校验 —— 前端能被绕过，后端那份才是权威。
	if (!file.type.startsWith('image/')) throw new Error('请选择图片文件')
	if (file.size > 3 * 1024 * 1024) throw new Error('图片不能超过 3MB')

	const form = new FormData()
	form.append('file', file)
	// ⚠️ 不能设 Content-Type: multipart/form-data —— 边界由浏览器生成，
	// 手动设置会写出错误的 boundary，表现为后端收不到文件。
	const res = await fetch('/api/profile/avatar', {
		method: 'POST',
		credentials: 'include',
		headers: {'X-CSRFToken': readCsrf()},
		body: form,
	})
	const body = await res.json().catch(() => null)
	if (!res.ok || !body?.ok) {
		throw new Error(body?.error || `上传失败（HTTP ${res.status}）`)
	}
	return (body.data.avatar_url as string) + `?t=${Date.now()}`
}

/** 清除头像（删库里的引用；磁盘上的文件留着不碍事，也会被下次上传覆盖）。 */
export async function clearAvatar(): Promise<void> {
	await api.post('/profile/avatar/clear')
}

// ---------------------------------------------------------------------------
// 音高识别校准参数（视弹/跟弹）
// ---------------------------------------------------------------------------

export type PitchCalibration = {
	/** 期望音判命中的置信度下限 */
	hitThreshold?: number
	/** 谐波模板非谐性系数 */
	inharmonicity?: number
	/** 谐波衰减系数 */
	decay?: number
	/** 最近一次校准时间（ISO 串） */
	calibratedAt?: string
}

/** 读当前用户的音高校准参数；未校准返回空对象。 */
export async function fetchPitchCalibration(): Promise<PitchCalibration> {
	try {
		return await api.get<PitchCalibration>('/profile/pitch-calibration')
	} catch {
		// 读不到（后端旧版/未登录）回落空，识别引擎用默认参数
		return {}
	}
}

/** 保存音高校准参数（整段覆盖）。 */
export async function savePitchCalibration(calib: PitchCalibration): Promise<void> {
	await api.post<PitchCalibration>('/profile/pitch-calibration/save', calib)
}

function readCsrf(): string {
	const hit = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))
	return hit ? decodeURIComponent(hit.slice('csrftoken='.length)) : ''
}